'use client'

import { useClientPageProps, type ClientPageProps } from '@/lib/client-page-dynamic'

import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { LoadingImage } from '@/components/LoadingImage'
import Link from 'next/link'
import toast from 'react-hot-toast'
import axios from 'axios'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api/v1'

/**
 * Self-service password reset request.
 * Calls POST /api/v1/auth/forgot-password with { email }.
 * The API always reports success when the address is unknown, so this page
 * shows the same confirmation either way. A 503 means the email could not be sent.
 */
export default function ForgotPasswordPage(props: ClientPageProps) {
    useClientPageProps(props)

    const [isLoading, setIsLoading] = useState(false)
    const [email, setEmail] = useState('')
    const [emailSent, setEmailSent] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)

        try {
            await axios.post(`${API_URL}/auth/forgot-password`, { email })
            setEmailSent(true)
            toast.success('If an account exists for that email, reset instructions have been sent')
        } catch (error: any) {
            toast.error(error.response?.data?.error || 'Failed to send reset email')
        } finally {
            setIsLoading(false)
        }
    }

    if (emailSent) {
        return (
            <div className="flex flex-col justify-center items-center px-6 pt-8 mx-auto md:h-screen pt:mt-0 safe-top safe-bottom">
                <a href="/hosting-control-panel" className="flex justify-center items-center mb-8 text-2xl font-semibold lg:mb-10">
                    <span className="self-center text-2xl font-bold whitespace-nowrap">Hosting Control Panel</span>
                </a>
                <div className="p-10 w-full max-w-lg bg-white rounded-2xl shadow-xl shadow-gray-300">
                    <div className="space-y-8">
                        <h2 className="text-2xl font-bold text-gray-900">
                            Check Your Email
                        </h2>
                        <p className="text-gray-600">
                            If an account exists for {email}, we sent a link to choose a new password. The link expires in one hour.
                        </p>
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600 text-center">
                                Didn&apos;t receive the email? Check your spam folder or try again.
                            </p>
                            <button
                                onClick={() => {
                                    setEmailSent(false)
                                    setEmail('')
                                }}
                                className="py-3 px-5 w-full text-base font-medium text-center text-white bg-gradient-to-br from-pink-500 to-voilet-500 hover:scale-[1.02] shadow-md shadow-gray-300 transition-transform rounded-lg"
                            >
                                Try Again
                            </button>
                            <Link href="/auth/login">
                                <button className="py-3 px-5 w-full text-base font-medium text-center text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 shadow-sm transition-transform rounded-lg flex items-center justify-center gap-2">
                                    <ArrowLeft size={18} />
                                    Back to Login
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col justify-center items-center px-6 pt-8 mx-auto md:h-screen pt:mt-0 safe-top safe-bottom">
            <a href="/hosting-control-panel" className="flex justify-center items-center mb-8 text-2xl font-semibold lg:mb-10">
                <span className="self-center text-2xl font-bold whitespace-nowrap">Hosting Control Panel</span>
            </a>
            <div className="p-10 w-full max-w-lg bg-white rounded-2xl shadow-xl shadow-gray-300">
                <div className="space-y-8">
                    <h2 className="text-2xl font-bold text-gray-900">
                        Forgot Password?
                    </h2>
                    <p className="text-gray-600">
                        Enter your email address and we&apos;ll send you a link to reset your password
                    </p>
                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900">Your email</label>
                            <input
                                type="email"
                                name="email"
                                id="email"
                                className="border border-gray-300 text-gray-900 sm:text-sm rounded-lg focus:ring-2 focus:ring-fuchsia-50 focus:border-fuchsia-300 block w-full p-2.5"
                                placeholder="name@company.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={isLoading}
                            />
                        </div>
                        <button type="submit" disabled={isLoading} className="py-3 px-5 w-full text-base font-medium text-center text-white bg-gradient-to-br from-pink-500 to-voilet-500 hover:scale-[1.02] shadow-md shadow-gray-300 transition-transform rounded-lg sm:w-auto flex items-center justify-center gap-2">
                            {isLoading ? (
                                <>
                                    <LoadingImage size={18} ariaLabel="Sending" />
                                    Sending...
                                </>
                            ) : (
                                'Send Reset Instructions'
                            )}
                        </button>
                        <div className="text-sm font-medium text-gray-500">
                            <Link href="/auth/login" className="text-[#27598e] hover:underline flex items-center justify-center gap-2">
                                <ArrowLeft size={16} />
                                Back to Login
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}
